import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * ModernInput - Clean, Minimal Input Component
 * Consistent styling across the template builder
 */
const ModernInput = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required = false,
  className = "",
  description = "",
  icon: Icon = null,
  validationRules = {},
  fieldPath = "",
  showValidation = true,
  validationErrors = {},
  setValidationErrors = () => {},
  disabled = false
}) => {
  const error = validationErrors[fieldPath];
  const hasError = error && showValidation;

  const validateField = (path, value, rules = {}) => {
    const errors = [];
    if (rules.required && (!value || value.toString().trim() === '')) {
      errors.push('This field is required');
    }
    if (rules.email && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      errors.push('Invalid email format');
    }
    if (rules.phone && value && !/^[\+]?[0-9\s\-\(\)]{10,}$/.test(value.replace(/\s/g, ''))) {
      errors.push('Invalid phone number');
    }
    if (rules.url && value && !/^https?:\/\/.+\..+/.test(value)) {
      errors.push('Invalid URL format');
    }
    if (rules.minLength && value && value.length < rules.minLength) {
      errors.push(`Minimum ${rules.minLength} characters required`);
    }
    if (rules.maxLength && value && value.length > rules.maxLength) {
      errors.push(`Maximum ${rules.maxLength} characters allowed`);
    }
    return errors.length > 0 ? errors[0] : null;
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className={`h-4 w-4 ${hasError ? 'text-red-400' : 'text-gray-400'}`} />
          </div>
        )}
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => {
            if (fieldPath && validationRules && Object.keys(validationRules).length > 0) {
              const error = validateField(fieldPath, e.target.value, validationRules);
              if (error) {
                setValidationErrors(prev => ({ ...prev, [fieldPath]: error }));
              } else {
                setValidationErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[fieldPath];
                  return newErrors;
                });
              }
            }
          }}
          disabled={disabled}
          className={`
            w-full px-3 py-2.5 text-sm
            border rounded-lg
            transition-all duration-150
            placeholder:text-gray-400
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${Icon ? 'pl-9' : ''}
            ${hasError
              ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
              : 'border-gray-300 bg-white hover:border-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/10'
            }
            focus:outline-none
          `}
          placeholder={placeholder}
        />
        {hasError && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <AlertCircle className="h-4 w-4 text-red-400" />
          </div>
        )}
      </div>
      {hasError && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  );
};

export default ModernInput;
